import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/data/queryKeys';
import { aiOrgService } from '../services/aiOrgService';
import type { ChiefStreamCallbacks } from '../services/aiOrgService';
import type { ProposalFilter, ProposalReviewBesluit } from '../types';

export type { ChiefStreamCallbacks };

/** Registry-lijst: alle agents, gesorteerd op domein + code. */
export function useAgents() {
  return useQuery({
    queryKey: queryKeys.aiOrg.agents,
    queryFn: () => aiOrgService.listAgents(),
  });
}

/** Eén agent-dossier op code. */
export function useAgent(code: string | undefined) {
  return useQuery({
    queryKey: queryKeys.aiOrg.agent(code ?? ''),
    queryFn: () => aiOrgService.getAgent(code as string),
    enabled: !!code,
  });
}

export function useInstructieVersies(code: string | undefined) {
  return useQuery({
    queryKey: queryKeys.aiOrg.instructieVersies(code ?? ''),
    queryFn: () => aiOrgService.listInstructieVersies(code as string),
    enabled: !!code,
  });
}

/** Kosten deze maand + laatste run, per agent (voor lijst + statcards). */
export function useRunStats() {
  return useQuery({
    queryKey: queryKeys.aiOrg.runStats,
    queryFn: () => aiOrgService.runStats(),
    staleTime: 60_000,
  });
}

/** Prestaties van één agent over de laatste 30 dagen. */
export function useAgentPrestaties(code: string | undefined) {
  return useQuery({
    queryKey: queryKeys.aiOrg.prestaties(code ?? ''),
    queryFn: () => aiOrgService.agentPrestaties(code as string),
    enabled: !!code,
  });
}

export function useProposals(filter: ProposalFilter) {
  return useQuery({
    queryKey: queryKeys.aiOrg.proposals(filter),
    queryFn: () => aiOrgService.listProposals(filter),
  });
}

/**
 * Aantal openstaande voorstellen (badge in het menu). Alleen inschakelen voor
 * Admin: RLS geeft anderen toch niets terug.
 */
export function useOpenProposalsCount(enabled: boolean) {
  const query = useQuery({
    queryKey: queryKeys.aiOrg.openProposalsCount,
    queryFn: () => aiOrgService.countOpenProposals(),
    enabled,
    refetchInterval: 60_000,
  });
  return { count: query.data ?? 0 };
}

/** Goedkeuren/afwijzen van een voorstel; ververst inbox, badge en dossiers. */
export function useProposalReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      besluit: ProposalReviewBesluit;
      reviewerId: string;
      reden?: string;
    }) => aiOrgService.reviewProposal(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ai_org', 'proposals'] });
      qc.invalidateQueries({ queryKey: ['ai_org', 'prestaties'] });
    },
  });
}

/**
 * Goedgekeurd voorstel daadwerkelijk uitvoeren (edge function
 * `voer-voorstel-uit`). Ververst inbox, badge en dossiers — ook bij een 501
 * ("type nog niet ondersteund"), want de status kan intussen gewijzigd zijn.
 */
export function useVoerVoorstelUit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (proposalId: string) => aiOrgService.voerVoorstelUit(proposalId),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['ai_org', 'proposals'] });
      qc.invalidateQueries({ queryKey: ['ai_org', 'prestaties'] });
    },
  });
}

/** Status van de Chief-spraakinrichting (Admin-only sectie "Chief-stem"). */
export function useChiefVoiceSetup(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.aiOrg.voiceSetup,
    queryFn: () => aiOrgService.voiceSetupStatus(),
    enabled,
    staleTime: 60_000,
  });
}

/**
 * Genereer stemkandidaten (Voice Design); resultaat toont de kaarten.
 * Zonder argument alle vier; met stijl-slot-indices (0–3) alleen die slots
 * ("vervang niet-bewaarde stemmen" houdt de bewaarde kaarten vast).
 */
export function useGenereerStemKandidaten() {
  return useMutation({
    mutationFn: (indices?: number[]) => aiOrgService.voiceKandidaten(indices),
  });
}

/** "Deze wordt Chief": kandidaat definitief maken; ververst de setup-status. */
export function useKiesStem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { generatedVoiceId: string; omschrijving: string }) =>
      aiOrgService.kiesVoice(input.generatedVoiceId, input.omschrijving),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.aiOrg.voiceSetup });
    },
  });
}

/** Bestaande bibliotheek-stem (voice-id) direct als Chief instellen. */
export function useKiesBestaandeStem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (voiceId: string) => aiOrgService.kiesBestaandeStem(voiceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.aiOrg.voiceSetup });
    },
  });
}

/** Spraakprovider wisselen (ElevenLabs / OpenAI-fallback). */
export function useZetVoiceProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (provider: 'elevenlabs' | 'openai') => aiOrgService.zetVoiceProvider(provider),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.aiOrg.voiceSetup });
    },
  });
}

/** Ingerichte spraakagent bijwerken met de actuele persona en tools. */
export function useSyncVoiceAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => aiOrgService.syncVoiceAgent(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.aiOrg.voiceSetup });
    },
  });
}

/**
 * Vraag aan Chief via de `chief`-edge function, met SSE-streaming. Deltas en
 * status komen live binnen via de meegegeven callbacks; het resultaat van de
 * mutation is de volledige antwoordtekst. Faalt het streamen vóór de eerste
 * delta (bijv. oudere function-versie of proxy zonder SSE), dan vallen we
 * geruisloos terug op het niet-streaming pad.
 */
export function useChiefVraag() {
  return useMutation({
    mutationFn: async (input: {
      vraag: string;
      historie: Array<{ role: 'user' | 'assistant'; content: string }>;
      callbacks?: ChiefStreamCallbacks;
    }) => {
      let deltaGezien = false;
      try {
        return await aiOrgService.vraagChiefStream(input.vraag, input.historie, {
          onStatus: input.callbacks?.onStatus,
          onDelta: (text) => {
            deltaGezien = true;
            input.callbacks?.onDelta?.(text);
          },
          onReset: input.callbacks?.onReset,
        });
      } catch (err) {
        // Al tekst getoond? Dan is stil opnieuw proberen verwarrender dan de fout.
        if (deltaGezien) throw err;
        return aiOrgService.vraagChief(input.vraag, input.historie);
      }
    },
  });
}
